'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || '';

// ── Persona Milieu Detection ─────────────────────────────────────
// Collects PostHog signals (referrer, device, geo, UTM, page) and
// computes a milieu probability vector stored as person properties.
// Enables feature flag targeting by persona milieu.

const REFERRAL_MILIEU: Record<string, Record<string, number>> = {
  'instagram.com': { expeditiv: 0.30, 'neo-oekologisch': 0.10 },
  'l.instagram.com': { expeditiv: 0.30, 'neo-oekologisch': 0.10 },
  'tiktok.com': { expeditiv: 0.35 },
  'facebook.com': { 'konservativ-gehoben': 0.20, 'adaptiv-pragmatisch': 0.15 },
  'l.facebook.com': { 'konservativ-gehoben': 0.20, 'adaptiv-pragmatisch': 0.15 },
  'lm.facebook.com': { 'konservativ-gehoben': 0.20, 'adaptiv-pragmatisch': 0.15 },
  'google.com': { postmateriell: 0.15, performer: 0.15 },
  'google.de': { postmateriell: 0.15, performer: 0.15 },
  'linkedin.com': { performer: 0.30 },
  'pinterest.com': { expeditiv: 0.15, postmateriell: 0.10 },
  'youtube.com': { performer: 0.10, expeditiv: 0.10 },
};

const MAJOR_CITIES = ['berlin', 'hamburg', 'münchen', 'köln', 'frankfurt', 'düsseldorf', 'stuttgart', 'leipzig', 'dresden', 'hannover', 'nürnberg', 'bremen', 'dortmund', 'essen'];

function detectPersonaMilieu() {
  if (!posthog.__loaded) return;

  const scores: Record<string, number> = {
    postmateriell: 0,
    'konservativ-gehoben': 0,
    performer: 0,
    expeditiv: 0,
    'neo-oekologisch': 0,
    'adaptiv-pragmatisch': 0,
  };
  const signals: string[] = [];

  // 1. Referral domain
  const ref = posthog.get_property('$referring_domain') as string | undefined;
  if (ref && REFERRAL_MILIEU[ref]) {
    for (const [m, w] of Object.entries(REFERRAL_MILIEU[ref])) {
      scores[m] = (scores[m] || 0) + w;
    }
    signals.push('ref:' + ref);
  }

  // 2. UTM source
  const utm = posthog.get_property('utm_source') as string | undefined;
  if (utm) {
    const u = utm.toLowerCase();
    if (u.includes('instagram')) { scores.expeditiv += 0.25; signals.push('utm:ig'); }
    else if (u.includes('facebook')) { scores['konservativ-gehoben'] += 0.15; scores['adaptiv-pragmatisch'] += 0.10; signals.push('utm:fb'); }
    else if (u.includes('tierarzt')) { scores['konservativ-gehoben'] += 0.30; signals.push('utm:vet'); }
    else if (u.includes('podcast')) { scores.postmateriell += 0.25; signals.push('utm:pod'); }
    else if (u.includes('influencer')) { scores.expeditiv += 0.30; signals.push('utm:inf'); }
    else if (u.includes('linkedin')) { scores.performer += 0.25; signals.push('utm:li'); }
  }

  // 3. Device type
  const dev = posthog.get_property('$device_type') as string | undefined;
  if (dev === 'Mobile') {
    scores.expeditiv += 0.15;
    scores.performer += 0.05;
    signals.push('dev:mob');
  } else if (dev === 'Desktop') {
    scores.postmateriell += 0.10;
    scores['konservativ-gehoben'] += 0.10;
    signals.push('dev:desk');
  }

  // 4. Geographic (city type)
  const city = posthog.get_property('$geoip_city_name') as string | undefined;
  if (city) {
    const isUrban = MAJOR_CITIES.some(c => city.toLowerCase().includes(c));
    if (isUrban) {
      scores.expeditiv += 0.10;
      scores.performer += 0.10;
      signals.push('geo:urban');
    } else {
      scores['konservativ-gehoben'] += 0.10;
      scores['adaptiv-pragmatisch'] += 0.05;
      signals.push('geo:rural');
    }
  }

  // 5. Current page path signals
  const path = window.location.pathname.replace(/^\/[a-z]{2}\//, '/');
  if (path.includes('/ernaehrungskonzept')) { scores.postmateriell += 0.20; signals.push('page:science'); }
  if (path.includes('/futterassistent')) { scores.performer += 0.10; signals.push('page:funnel'); }
  if (path.includes('/nachhaltigkeit')) { scores['neo-oekologisch'] += 0.25; signals.push('page:eco'); }
  if (path.includes('/ueber-uns')) { scores.postmateriell += 0.10; scores['konservativ-gehoben'] += 0.10; signals.push('page:about'); }
  if (path.includes('/trockenfutter')) { scores['adaptiv-pragmatisch'] += 0.10; signals.push('page:product'); }

  // 6. Session duration
  const sessionDur = posthog.get_property('$session_duration') as number | undefined;
  if (sessionDur && sessionDur > 300) { scores.postmateriell += 0.15; signals.push('sess:long'); }
  else if (sessionDur && sessionDur < 120) { scores.performer += 0.15; signals.push('sess:short'); }

  // Need at least 2 signals for meaningful detection
  if (signals.length < 2) return;

  // Normalize scores to probabilities
  const total = Object.values(scores).reduce((s, v) => s + v, 0);
  if (total === 0) return;
  for (const k of Object.keys(scores)) {
    scores[k] = Math.round((scores[k] / total) * 100) / 100;
  }

  const sorted = Object.entries(scores).sort(([, a], [, b]) => b - a);
  const primary = sorted[0][0];
  const confidence = sorted[0][1];

  // Build human-readable reasoning for each signal
  const signalReasons: Record<string, string> = {
    'ref:instagram.com': 'Instagram-Referral → digital-native, visuell-orientiert',
    'ref:l.instagram.com': 'Instagram-Referral → digital-native, visuell-orientiert',
    'ref:tiktok.com': 'TikTok-Referral → Gen Z, trend-orientiert',
    'ref:facebook.com': 'Facebook-Referral → traditioneller, community-orientiert',
    'ref:l.facebook.com': 'Facebook-Referral → traditioneller, community-orientiert',
    'ref:lm.facebook.com': 'Facebook-Referral → traditioneller, community-orientiert',
    'ref:google.com': 'Google-Suche → recherchiert aktiv, vergleicht',
    'ref:google.de': 'Google-Suche → recherchiert aktiv, vergleicht',
    'ref:linkedin.com': 'LinkedIn-Referral → karriereorientiert, business-affin',
    'ref:pinterest.com': 'Pinterest-Referral → visuell, inspirations-suchend',
    'ref:youtube.com': 'YouTube-Referral → Video-orientiert, recherchiert',
    'utm:ig': 'UTM Instagram-Kampagne → social-media-affin',
    'utm:fb': 'UTM Facebook-Kampagne → community/traditional reach',
    'utm:vet': 'UTM Tierarzt-Empfehlung → autoritäts-orientiert',
    'utm:pod': 'UTM Podcast-Kampagne → bildungsorientiert, deep-content',
    'utm:inf': 'UTM Influencer-Kampagne → trend-/community-getrieben',
    'utm:li': 'UTM LinkedIn-Kampagne → business-/effizienz-orientiert',
    'dev:mob': 'Mobile Device → digital-native, unterwegs',
    'dev:desk': 'Desktop Device → gründlich, zeit-investierend',
    'geo:urban': 'Großstadt → modern, divers, innovationsaffin',
    'geo:rural': 'Ländlich/Kleinstadt → traditionell, bodenständig',
    'page:science': 'Besuchte /ernaehrungskonzept → wissenschaftsinteressiert',
    'page:funnel': 'Besuchte /futterassistent → direkte Kaufabsicht',
    'page:eco': 'Besuchte /nachhaltigkeit → umweltbewusst',
    'page:about': 'Besuchte /ueber-uns → markeninteressiert, vertrauenssuchend',
    'page:product': 'Besuchte /trockenfutter → produktorientiert, preisvergleichend',
    'sess:long': 'Lange Session (>5 Min) → recherchiert gründlich',
    'sess:short': 'Kurze Session (<2 Min) → entscheidet schnell',
  };

  const reasons = signals.map(s => ({
    signal: s,
    reason: signalReasons[s] || s,
  }));

  // Milieu display names
  const milieuNames: Record<string, string> = {
    postmateriell: 'Postmateriell (gebildet, werteorientiert, wissenschaftsaffin)',
    'konservativ-gehoben': 'Konservativ-Gehoben (wohlhabend, traditionsbewusst, markentreu)',
    performer: 'Performer (effizient, datengetrieben, ergebnisorientiert)',
    expeditiv: 'Expeditiv (digital-native, kreativ, emotional)',
    'neo-oekologisch': 'Neo-Ökologisch (nachhaltig, aktivistisch, transparent)',
    'adaptiv-pragmatisch': 'Adaptiv-Pragmatisch (pragmatisch, preisbewusst, lösungsorientiert)',
  };

  // Store as PostHog person properties (enables feature flag targeting)
  posthog.setPersonProperties({
    persona_primary: primary,
    persona_primary_label: milieuNames[primary] || primary,
    persona_confidence: confidence,
    persona_scores: JSON.stringify(scores),
    persona_top3: JSON.stringify(sorted.slice(0, 3)),
    persona_top3_labels: sorted.slice(0, 3).map(([m, s]) => `${milieuNames[m]?.split(' (')[0] || m}: ${Math.round(s * 100)}%`).join(' | '),
    persona_signals_count: signals.length,
    persona_signals_detail: JSON.stringify(reasons),
    persona_detected_at: new Date().toISOString(),
  });

  // Emit custom event for dashboard visibility
  posthog.capture('persona_detected', {
    milieu_primary: primary,
    milieu_primary_label: milieuNames[primary] || primary,
    milieu_confidence: confidence,
    milieu_confidence_pct: Math.round(confidence * 100) + '%',
    milieu_top3: sorted.slice(0, 3).map(([m, s]) => `${milieuNames[m]?.split(' (')[0] || m}: ${Math.round(s * 100)}%`).join(' | '),
    milieu_scores: scores,
    signals_count: signals.length,
    signals: reasons,
    detection_reason: `${signals.length} Signale analysiert: ${reasons.map(r => r.reason).join('; ')}`,
  });
}

// ── PostHog Provider ─────────────────────────────────────────────

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (!POSTHOG_KEY || !POSTHOG_HOST) return;
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
      enable_heatmaps: true,
      enable_recording_console_log: false,
      persistence: 'localStorage+cookie',
    });

    // Identify authenticated users
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user && posthog.__loaded) {
        posthog.identify(user.id, {
          email: user.email,
          name: user.user_metadata?.full_name || user.user_metadata?.name,
        });
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!posthog.__loaded) return;
      if (event === 'SIGNED_IN' && session?.user) {
        posthog.identify(session.user.id, {
          email: session.user.email,
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
        });
      } else if (event === 'SIGNED_OUT') {
        posthog.reset();
      }
    });

    // Run initial persona detection after 5 seconds
    // (allows PostHog to collect referrer, device, geo signals)
    const personaTimer = setTimeout(detectPersonaMilieu, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(personaTimer);
    };
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}

// ── Pageview Tracker ─────────────────────────────────────────────

export function PostHogPageview() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pathname || !POSTHOG_KEY) return;
    const url = searchParams?.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname;
    posthog.capture('$pageview', { $current_url: window.location.origin + url });

    // Re-run persona detection on navigation (signals accumulate with each page)
    setTimeout(detectPersonaMilieu, 2000);
  }, [pathname, searchParams]);

  return null;
}
